<template>
    <div>
        <!-- Everything the printed receipt should carry lives inside this wrapper: util/printElement
             copies only this element's innerHTML into the print document, so the action buttons below
             are excluded by being siblings of it. -->
        <div id="shippingTicketPrintArea">
            <b-row>
                <b-col>
                    <b>Receipt:</b>
                    <b-badge v-if="hasUrgentPrescription" variant="danger" class="ml-2">URGENT</b-badge>
                    <br>
                    <span>{{ order.patient.displayNamesForPerson() }}</span><br>
                    <span>{{ value.fullAddress }}</span><br>
                </b-col>
                <b-col>
                    <b>Verified: </b> <span>{{ value.verified | formatDate('LLL') }}</span><br>
                    <b>Verified By: </b> <span>{{ value.verifiedBy }}</span><br>
                    <b>Shipped: </b> <span>{{ value.shipped | formatDate('LLL') }}</span><br>
                    <b>Shipped By: </b> <span>{{ value.shippedBy }}</span><br>
                </b-col>
            </b-row>

            <b-row v-if="order.promisedDate" class="mt-2">
                <b-col>
                    <b>Promise Date: </b>
                    <promised-time-summary :value="order.promisedDate" info-only />
                    <span class="ml-1">{{ order.promisedDate | formatDate('LLL') }}</span>
                </b-col>
            </b-row>

            <h4>Scripts</h4>
            <b-row>
                <b-col>
                    <!-- <table></table> -->
                    <b-table :items="prescriptions" :fields="rxFields">
                        <template #cell(productNameShort)="data">
                            {{ data.value }}
                            <b-badge v-if="data.item.needsRefrigeration" variant="info" class="ml-1">
                                <i class="fas fa-snowflake"></i> REFRIGERATED
                            </b-badge>
                        </template>
                    </b-table>
                </b-col>
            </b-row>

            <h4>Add Ons</h4>
            <b-row>
                <b-col>
                    <!-- <table></table> -->
                    <b-table :items="order.items" :fields="otcFields"></b-table>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <b>Tracking Number:</b><br>
                    <span>{{ value.trackingNumber }}</span>
                </b-col>
                <b-col>
                    <b>Ship Date:</b><br>
                    <span>{{ value.verified | formatDate('LL') }}</span>
                </b-col>
                <b-col>
                    <b>Shipping Station:</b><br>
                    <span>{{ value.shippingStation }}</span>
                </b-col>
            </b-row>

            <h4 class="mt-3">Payments</h4>
            <b-row>
                <b-col>
                    <!-- Gated on loading so the receipt never shows an empty state or a $0.00 total while
                         the fetches are still in flight, and never commits to either if one failed. -->
                    <div v-if="loading">Loading payment information&hellip;</div>

                    <div v-else-if="orderUnavailable">
                        Payment information could not be loaded for this order. Reopen the ticket to try again.
                    </div>

                    <template v-else>
                        <b-table v-if="paymentRows.length" :items="paymentRows" :fields="paymentFields" small></b-table>
                        <span v-else>No payment recorded for this order.</span>

                        <!-- Labelled "on this order" on purpose: the Scripts table above prints per-Rx copays,
                             which legitimately differ from what was collected once insurance pays part. -->
                        <div class="mt-2"><b>Total Paid on This Order: </b>{{ totalPaidDisplay }}</div>

                        <div v-if="cardDetailsIncomplete" class="mt-1">
                            <small>Card brand and last four digits are unavailable for one or more card payments;
                                the amounts above are from this order's payment records.</small>
                        </div>

                        <div v-if="hasUncapturedAuthorization" class="mt-1">
                            <small>An authorized amount shown above was never captured and is excluded from the
                                total paid.</small>
                        </div>
                    </template>
                </b-col>
            </b-row>

            <b-row v-if="value && value.deliveryConfirmationDate">
                <b-col>
                    <strong>Patient Delivery Attestation</strong><br />
                    {{ value.deliveryConfirmationAttestationLanguage }}<br />
                    <div v-html="signatureHtml" />
                    <br />
                    <!--            <b-img :src="value.deliveryConfirmationSignature" alt="Signature"/><br/>-->
                    {{ order.patient.firstName }} {{ order.patient.lastName }}<br />
                    Date Signed: {{ value.deliveryConfirmationDate | formatLocalDate('LLL') }}
                </b-col>
            </b-row>
        </div>

        <div class="d-flex mt-4 d-print-none" style="gap: 10px;">
            <b-button variant="success" :disabled="loading" @click="printTicket" v-b-tooltip.hover.ds750
                title="Prints a paper receipt of this shipping ticket using your browser's print dialog. This is not a pharmacy label.">
                <i class="fa fa-print"></i>
                Print Receipt
            </b-button>

            <!--    Only display the send tracking text for orders that are not Will Call orders    -->
            <b-button v-if="value.shipmentType !== 'Will-Call'" @click="sendTrackingText(value.trackingNumber)"
                variant="info">
                <i class="fa fa-paper-plane"></i>
                Send Tracking Text
            </b-button>

            <b-button @click="openDeliveryConfirmationImage" v-if="value.deliveryProofImageId">
                <i class="fa fa-download"></i>
                Download Delivery Confirmation
            </b-button>

            <b-button
                v-if="value.voided && $permissions.some(p => p.permissionCodeName === 'Admin/Unvoid Shipment')"
                @click="unvoidShipment"
                variant="warning">
                <i class="fa fa-undo"></i>
                Unvoid Shipment
            </b-button>
        </div>
    </div>
</template>

<script lang="ts">

import { Component, Prop, Vue } from 'vue-property-decorator';
import { Shipment } from '@/models/Shipment';
import { Order } from '@/models/Order'
import moment from "moment";
import numeral from "numeral";
import { Patient } from "@/models/Patient";
import { StripeCard } from "@/models/Payment";
import axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import PromisedTimeSummary from '@/components/Order/PromisedTimeSummaryComponent.vue';
import {
    buildReceiptPaymentRows,
    receiptHasCardDetailGap,
    receiptHasUncapturedAuthorization,
    receiptTotalPaid,
    ReceiptPaymentRow,
} from '@/util/receiptPayments';

@Component({
    components: { PromisedTimeSummary }
})
export default class ShippingTicketComponent extends Vue {

    @Prop() private value!: Shipment;
    private order: Order = new Order();

    @Prop({ required: true }) patient!: Patient;

    private rxFields: any[] = [
        { key: "storeName", label: "Store" },
        { key: "rxId", label: "Rx" },
        { key: "productNameShort", label: "Drug" },
        { key: "prescriberName", label: "Prescriber" },
        { key: "dispensed", label: "Qty", class: "text-center", },
        { key: "patientPay", label: "Copay", class: "text-center", formatter: this.moneyFormatter },
        { key: "fillDate", label: "Fill Date", formatter: this.dateFormatter },
        { key: "insurance", label: "PlanNames" },
    ];

    private otcFields: any[] = [
        { key: "otcItemName", label: "Product Name", },
        { key: "otcItemUPC", label: "UPC", },
        { key: "quantity", label: "Qty", },
        { key: "price", label: "Price", formatter: this.moneyFormatter },
        { key: "tax", label: "Tax", },
    ];

    private paymentFields: any[] = [
        { key: "method", label: "Payment Method" },
        { key: "detail", label: "Card / Reference" },
        { key: "expiration", label: "Exp" },
        { key: "amount", label: "Amount", class: "text-right", formatter: this.moneyFormatter },
    ];

    private stripeCards: StripeCard[] = [];
    // Covers both fetches, so the receipt cannot be printed half-populated.
    private loading = true;
    private paymentsUnavailable = false;
    // The order fetch leaves `order` as an empty Order on failure, which is indistinguishable from an
    // order with no tenders. Tracked so the receipt reports a load failure instead of asserting $0.00.
    private orderUnavailable = false;

    created() {
        this.getOrderForShipping();
    }

    get hasUrgentPrescription(): boolean {
        return this.order?.prescriptions?.some(rx => rx.isUrgent) ?? false;
    }

    async getOrderForShipping() {
        try {
            const response = await axios.get<Order>(`/Shipping/order/${this.value.id}`);
            this.order = new Order(response.data);
            // Chained rather than parallel: the modal is handed a shipment, so the order id the
            // charges are keyed by only exists once this response lands.
            await this.loadCardCharges();
        } catch (error) {
            this.orderUnavailable = true;
            console.error("Error while loading order information in shipping ticket", { error, response: (error as any)?.response });
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.loading = false;
        }
    }

    /**
     * Card brand and last four are not stored locally, so they have to come back from Stripe. Only
     * orders that actually carry a card need that round trip; cash, check, and AR amounts are
     * already in the order payload.
     */
    async loadCardCharges() {
        const hasStripeTender = this.order.payments?.some(p => {
            const chargeId = (p.chargeId ?? "").toLowerCase();
            return !!chargeId && !chargeId.startsWith("cash") && !chargeId.startsWith("check");
        }) ?? false;

        if (!this.order.id || !hasStripeTender) return;

        try {
            const response = await axios.get<StripeCard[]>(`/stripe/charges/${this.order.id}`);
            this.stripeCards = response.data ?? [];
        } catch (error) {
            // Stripe being unreachable must not block the receipt. Fall back to the tenders already
            // loaded and say so inside the print area — a toast never reaches paper.
            this.paymentsUnavailable = true;
            console.error("Error while loading card charges in shipping ticket", { error, response: (error as any)?.response });
        }
    }

    get paymentRows(): ReceiptPaymentRow[] {
        return buildReceiptPaymentRows(this.stripeCards, this.order.payments, this.order.arPosted);
    }

    get totalPaidDisplay(): string {
        return this.moneyFormatter(receiptTotalPaid(this.paymentRows));
    }

    /**
     * Covers a Stripe failure and a Stripe success that returned nothing for a card. Driven off the
     * rows rather than `paymentsUnavailable` alone, because a 200 with an empty list raises no error
     * yet still leaves a card printed without its brand and last four.
     */
    get cardDetailsIncomplete(): boolean {
        return this.paymentsUnavailable || receiptHasCardDetailGap(this.paymentRows);
    }

    get hasUncapturedAuthorization(): boolean {
        return receiptHasUncapturedAuthorization(this.paymentRows);
    }

    printTicket() {
        this.$htmlToPaper("shippingTicketPrintArea");
    }

    dateFormatter(value: Date, _key: string, _item: any) {
        return moment.utc(value).local().format("L");
    }

    moneyFormatter(value: number) {
        return numeral(value).format('$0,0.00');
    }

    get signatureHtml() {
        const sigData = this.value.deliveryConfirmationSignature;
        if (sigData != null && sigData.startsWith("<")) {
            return sigData;
        }
        return `<img src="${sigData}" alt="Signature"/>`;
    }

    get prescriptions(): Array<any> {
        if (!this.order?.prescriptions?.length) return [];

        return this.order.prescriptions.map(rx => {
            const res: any = rx;
            res.insurance = rx.claims?.length ? rx.claims[0].planName : ''; //Showing 1st claim plan name
            res.prescriberName = `${rx.prescriber.lastName}, ${rx.prescriber.firstName}`;
            return res;
        });
    }

    async sendTrackingText(urlString: String) {

        const payloadData = {
            MessageBody: urlString,
            Shipment: this.value,
            Patient: this.patient,
        };

        try {
            await axios.post('/Patient/SendPatientTrackingText', payloadData);
            this.$notification(NotificationOptions.success("Tracking Message Sent"));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async openDeliveryConfirmationImage() {
        const imageId = this.value.deliveryProofImageId;
        if (!imageId) return;
        try {
            const imageBlob = await axios.get(`/api/Image/${imageId}`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(imageBlob.data);
            window.open(objectUrl, "_blank");
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async unvoidShipment() {
        const confirmed = await this.$bvModal.msgBoxConfirm(
            'Are you sure you want to unvoid this shipment? This will restore the shipment to its previous state.',
            {
                title: 'Confirm Unvoid Shipment',
                okVariant: 'warning',
                okTitle: 'Unvoid',
                cancelTitle: 'Cancel',
                centered: true
            }
        );

        if (!confirmed) {
            return;
        }

        try {
            await axios.post(`/Shipping/UnvoidShipment/${this.value.id}`);
            this.$notification(NotificationOptions.success("Shipment unvoided successfully"));
            this.value.voided = false;
            this.$emit('shipment-unvoided', this.value);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }
}

</script>
